/**
 * Dados da empresa (`/app/empresa`) — a tela que o bloco da loja no topo abre.
 *
 * É SÓ IDENTIDADE: o que sai nos documentos. Três seções, na ordem do
 * handoff — identidade, inscrições fiscais, contato e endereço. Regime
 * tributário, alíquota, série e certificado NÃO estão aqui: são parâmetro
 * fiscal, moram em Configurações › Parâmetros fiscais, e misturar os dois
 * transformaria esta tela no depósito de todo campo que ninguém sabe onde pôr.
 *
 * O que não é editável aparece como DADO, com o motivo escrito — nunca como
 * campo desabilitado sem explicação. São dois casos:
 *
 *   * o CNPJ, que é a identidade fiscal e já saiu em nota;
 *   * o plano e as unidades, que não se mudam digitando (unidade é cadastro
 *     próprio, plano é assinatura).
 *
 * Nada aqui autoriza coisa nenhuma (CONSTITUICAO.md §4.1): o botão "Salvar"
 * aparece para todo mundo, e quem não tem `settings.fiscal` recebe a recusa do
 * porteiro no ponto da ação, com a mensagem dele.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from 'react'

import { Botao } from '../../componentes/Botao'
import { Segmentado } from '../../componentes/Segmentado'
import { Campo, MensagemDeErro } from '../../componentes/Campos'
import { Icone } from '../../componentes/Icone'
import { Selo } from '../../componentes/Selo'
import { ErroApi } from '../../plataforma/api'
import { buscarCEP } from '../../plataforma/auxiliares'
import {
  MAX_BYTES_LOGO,
  TIPOS_DE_LOGO,
  buscarEmpresa,
  enviarLogo,
  pedidoDe,
  removerLogo,
  salvarEmpresa,
  urlDaLogo,
  type Empresa,
  type PedidoEmpresa,
} from '../../plataforma/empresa'
import { useSessao } from '../../plataforma/contexto-sessao'
import {
  limparCEP,
  mascararCEP,
  mascararCNPJ,
  mascararTelefone,
  somenteDigitos,
} from '../../plataforma/mascaras'
import { LayoutApp } from '../app/LayoutApp'

/** As 27 unidades federativas, para o seletor de UF. */
const UFS = [
  'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT',
  'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO',
]

/** Erro por campo, no mesmo formato que a API devolve em `erro.campos`. */
type Erros = Record<string, string>

/** Máscara do CNAE: 4781400 → 4781-4/00. */
function mascararCNAE(bruto: string): string {
  const digitos = somenteDigitos(bruto).slice(0, 7)
  if (digitos.length <= 4) return digitos
  if (digitos.length <= 5) return `${digitos.slice(0, 4)}-${digitos.slice(4)}`
  return `${digitos.slice(0, 4)}-${digitos.slice(4, 5)}/${digitos.slice(5)}`
}

/** "148 KB" — o que a pessoa lê ao lado da miniatura da logo. */
function emKB(bytes: number): string {
  return `${Math.max(1, Math.round(bytes / 1024))} KB`
}

export function TelaDadosDaEmpresa() {
  const { revalidar } = useSessao()

  const [empresa, setEmpresa] = useState<Empresa | null>(null)
  const [rascunho, setRascunho] = useState<PedidoEmpresa | null>(null)
  const [erros, setErros] = useState<Erros>({})
  const [aviso, setAviso] = useState('')
  const [sucesso, setSucesso] = useState('')
  const [falhaAoCarregar, setFalhaAoCarregar] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [ocupadoComALogo, setOcupadoComALogo] = useState(false)
  const [buscandoCEP, setBuscandoCEP] = useState(false)

  const seletorDeArquivo = useRef<HTMLInputElement>(null)

  /** Guarda o que voltou da API e zera o formulário em cima dele. */
  const adotar = useCallback((nova: Empresa) => {
    setEmpresa(nova)
    setRascunho(pedidoDe(nova))
  }, [])

  useEffect(() => {
    const cancelamento = new AbortController()

    buscarEmpresa(cancelamento.signal)
      .then(adotar)
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setFalhaAoCarregar(
          causa instanceof ErroApi ? causa.message : 'Não foi possível carregar os dados da empresa.',
        )
      })

    return () => cancelamento.abort()
  }, [adotar])

  const alterar = <C extends keyof PedidoEmpresa>(campo: C, valor: PedidoEmpresa[C]) => {
    setRascunho((atual) => (atual ? { ...atual, [campo]: valor } : atual))
    setSucesso('')
  }

  /**
   * CEP completo preenche o endereço — mesmo comportamento do cadastro de
   * cliente e de fornecedor.
   *
   * SUBSTITUI o que estava lá: quem digitou outro CEP mudou de endereço, e
   * manter o bairro antigo produziria uma linha que não existe.
   */
  const aoMudarCEP = (texto: string) => {
    alterar('cep', somenteDigitos(texto).slice(0, 8))
    if (limparCEP(texto).length !== 8) return

    setBuscandoCEP(true)
    buscarCEP(texto)
      .then((endereco) => {
        if (!endereco) return
        setRascunho((atual) =>
          atual
            ? {
                ...atual,
                logradouro: endereco.logradouro || atual.logradouro,
                bairro: endereco.bairro || atual.bairro,
                cidade: endereco.cidade || atual.cidade,
                uf: endereco.uf || atual.uf,
              }
            : atual,
        )
      })
      .finally(() => setBuscandoCEP(false))
  }

  async function salvar() {
    if (!rascunho) return

    setErros({})
    setAviso('')
    setSucesso('')
    setSalvando(true)
    try {
      const salva = await salvarEmpresa(rascunho)
      adotar(salva)
      setSucesso('Dados da empresa salvos.')
      // O nome da loja aparece no topo do sistema, que lê a sessão. Sem
      // revalidar, a tela diz "salvo" e o cabeçalho continua com o nome antigo.
      void revalidar()
    } catch (causa: unknown) {
      if (!(causa instanceof ErroApi)) {
        setAviso('Não foi possível salvar os dados da empresa.')
        return
      }
      // A API devolve erro POR CAMPO — é isso que permite destacar o input em
      // que a pessoa digitou, em vez de um aviso solto no topo.
      setErros(causa.campos)
      setAviso(causa.message)
    } finally {
      setSalvando(false)
    }
  }

  async function aoEscolherArquivo(arquivo: File | undefined) {
    if (!arquivo) return

    setAviso('')
    setSucesso('')

    // Conferência local, que NÃO substitui a do servidor: ela existe para a
    // pessoa não esperar o upload de um arquivo que já se sabe que será
    // recusado.
    if (!TIPOS_DE_LOGO.includes(arquivo.type)) {
      setErros({ logo: 'A logo precisa ser PNG ou JPG.' })
      return
    }
    if (arquivo.size > MAX_BYTES_LOGO) {
      setErros({ logo: 'A logo pode ter no máximo 2 MiB.' })
      return
    }

    setErros({})
    setOcupadoComALogo(true)
    try {
      adotar(await enviarLogo(arquivo))
      setSucesso('Logo atualizada.')
    } catch (causa: unknown) {
      if (causa instanceof ErroApi) {
        setErros(causa.campos)
        setAviso(causa.message)
      } else {
        setAviso('Não foi possível enviar a logo.')
      }
    } finally {
      setOcupadoComALogo(false)
      if (seletorDeArquivo.current) seletorDeArquivo.current.value = ''
    }
  }

  async function apagarLogo() {
    setAviso('')
    setSucesso('')
    setOcupadoComALogo(true)
    try {
      adotar(await removerLogo())
      setSucesso('Logo removida.')
    } catch (causa: unknown) {
      setAviso(causa instanceof ErroApi ? causa.message : 'Não foi possível remover a logo.')
    } finally {
      setOcupadoComALogo(false)
    }
  }

  if (falhaAoCarregar) {
    return (
      <LayoutApp largura="formulario" titulo="Dados da empresa" subtitulo="Identidade que sai nos documentos">
        <div role="alert" className="rounded-sistema bg-erro-suave px-4 py-3 text-[13px] font-medium text-erro-texto">
          {falhaAoCarregar}
        </div>
      </LayoutApp>
    )
  }

  if (!empresa || !rascunho) {
    return (
      <LayoutApp largura="formulario" titulo="Dados da empresa" subtitulo="Identidade que sai nos documentos">
        <div className="h-[320px] w-full animate-pulse rounded-sistema bg-superficie" />
      </LayoutApp>
    )
  }

  return (
    <LayoutApp
      largura="formulario"
      titulo="Dados da empresa"
      subtitulo="Identidade que sai nos documentos"
      acoes={
        <Botao icone="check" disabled={salvando} onClick={() => void salvar()}>
          {salvando ? 'Salvando…' : 'Salvar'}
        </Botao>
      }
    >
      <form
        onSubmit={(evento) => {
          evento.preventDefault()
          void salvar()
        }}
        // O container ocupa a largura toda, como as outras telas do sistema —
        // quem dá o recuo lateral é o LayoutApp. A coluna de leitura
        // confortável é POR SEÇÃO (ver `Secao`), e não da tela inteira: um
        // formulário centralizado num sistema de barra lateral fica flutuando
        // no meio de dois vazios.
        className="flex w-full flex-col gap-4"
      >
        {aviso && (
          <div role="alert" className="rounded-sistema bg-erro-suave px-4 py-3 text-[13px] font-medium text-erro-texto">
            {aviso}
          </div>
        )}
        {sucesso && (
          <div role="status" className="rounded-sistema bg-ok-suave px-4 py-3 text-[13px] font-medium text-ok-texto">
            {sucesso}
          </div>
        )}

        {/* ------------------------------------------------ 01 · Identidade */}
        <Secao numero="01" titulo="Identidade" subtitulo="Como a empresa se identifica nos documentos">
          <div className="flex flex-col gap-5">
            <BlocoDaLogo
              empresa={empresa}
              ocupado={ocupadoComALogo}
              erro={erros.logo}
              seletor={seletorDeArquivo}
              aoEscolher={(arquivo) => void aoEscolherArquivo(arquivo)}
              aoRemover={() => void apagarLogo()}
            />

            {/*
              O CNPJ é DADO, não campo desabilitado: um input cinza sem
              explicação faz a pessoa tentar clicar, desistir e abrir chamado.
              A frase diz o que fazer.
            */}
            <div className="rounded-sistema border border-borda bg-superficie-suave px-4 py-3.5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-[12.5px] text-texto-suave">CNPJ</span>
                <Selo tom="neutro">
                  <Icone nome="lock" tamanho={12} />
                  Não editável
                </Selo>
              </div>
              <div className="tabular mt-1 text-[15px] font-semibold text-tinta">
                {mascararCNPJ(empresa.cnpj)}
              </div>
              <p className="mt-1.5 text-[12px] leading-[1.5] text-texto-fraco">
                O CNPJ é a identidade fiscal da loja e já saiu em nota emitida. Para mudar o CNPJ,
                fale com o suporte.
              </p>
            </div>

            <Campo
              rotulo="Razão social"
              value={rascunho.nome}
              onChange={(evento) => alterar('nome', evento.target.value)}
              placeholder="Cross Store Comércio de Artigos Esportivos Ltda"
              erro={erros.nome}
              dica="Nome jurídico, como está no CNPJ"
              required
            />

            <Campo
              rotulo="Nome fantasia"
              value={rascunho.nome_fantasia}
              onChange={(evento) => alterar('nome_fantasia', evento.target.value)}
              placeholder="Cross Store"
              erro={erros.nome_fantasia}
              dica="O cliente vê este nome"
            />

            <div>
              <span className="mb-2 block text-[13px] font-semibold text-texto">Tipo</span>
              <Segmentado
                opcoes={[
                  { chave: 'matriz', rotulo: 'Matriz' },
                  { chave: 'filial', rotulo: 'Filial' },
                ]}
                valor={rascunho.tipo_unidade}
                aoTrocar={(valor) => alterar('tipo_unidade', valor)}
              />
              {erros.tipo_unidade && <MensagemDeErro>{erros.tipo_unidade}</MensagemDeErro>}
            </div>
          </div>
        </Secao>

        {/* --------------------------------------- 02 · Inscrições fiscais */}
        <Secao
          numero="02"
          titulo="Inscrições fiscais"
          subtitulo="Usadas na emissão de NF-e e NFS-e"
        >
          <div className="flex flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                rotulo="Inscrição estadual"
                value={rascunho.inscricao_estadual}
                onChange={(evento) => alterar('inscricao_estadual', evento.target.value)}
                placeholder="ISENTO"
                erro={erros.inscricao_estadual}
              />
              <Campo
                rotulo="Inscrição municipal"
                value={rascunho.inscricao_municipal}
                onChange={(evento) => alterar('inscricao_municipal', evento.target.value)}
                placeholder="0000000"
                erro={erros.inscricao_municipal}
              />
            </div>

            <Campo
              rotulo="CNAE principal"
              inputMode="numeric"
              value={mascararCNAE(rascunho.cnae)}
              onChange={(evento) => alterar('cnae', somenteDigitos(evento.target.value).slice(0, 7))}
              placeholder="4781-4/00"
              erro={erros.cnae}
              dica="Código da atividade econômica principal"
            />

            <p className="border-t border-divisoria pt-3.5 text-[12px] leading-[1.5] text-texto-fraco">
              Regime tributário e alíquotas moram nas Configurações fiscais — aqui só a identidade.
            </p>
          </div>
        </Secao>

        {/* --------------------------------------- 03 · Contato e endereço */}
        <Secao numero="03" titulo="Contato e endereço" subtitulo="Saem no rodapé dos documentos">
          <div className="flex flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <Campo
                rotulo="Telefone"
                inputMode="numeric"
                value={mascararTelefone(rascunho.telefone)}
                onChange={(evento) => alterar('telefone', somenteDigitos(evento.target.value).slice(0, 11))}
                placeholder="(41) 3333-4444"
                erro={erros.telefone}
              />
              <Campo
                rotulo="WhatsApp"
                inputMode="numeric"
                value={mascararTelefone(rascunho.whatsapp)}
                onChange={(evento) => alterar('whatsapp', somenteDigitos(evento.target.value).slice(0, 11))}
                placeholder="(41) 99999-8888"
                erro={erros.whatsapp}
              />
              <Campo
                rotulo="E-mail público"
                type="email"
                value={rascunho.email_publico}
                onChange={(evento) => alterar('email_publico', evento.target.value)}
                placeholder="contato@crossstore.com.br"
                erro={erros.email_publico}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-[200px_minmax(0,1fr)]">
              <Campo
                rotulo="CEP"
                inputMode="numeric"
                value={mascararCEP(rascunho.cep)}
                onChange={(evento) => aoMudarCEP(evento.target.value)}
                placeholder="00000-000"
                erro={erros.cep}
                dica="Preenche o endereço automaticamente"
                acessorio={buscandoCEP ? <IndicadorBusca /> : undefined}
              />
              <Campo
                rotulo="Endereço"
                value={rascunho.logradouro}
                onChange={(evento) => alterar('logradouro', evento.target.value)}
                placeholder="Rua XV de Novembro, 1200"
                erro={erros.logradouro}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_120px]">
              <Campo
                rotulo="Bairro"
                value={rascunho.bairro}
                onChange={(evento) => alterar('bairro', evento.target.value)}
                placeholder="Centro"
                erro={erros.bairro}
              />
              <Campo
                rotulo="Cidade"
                value={rascunho.cidade}
                onChange={(evento) => alterar('cidade', evento.target.value)}
                placeholder="Curitiba"
                erro={erros.cidade}
              />
              <div>
                <label htmlFor="empresa-uf" className="mb-2 block text-[13px] font-semibold text-texto">
                  UF
                </label>
                <select
                  id="empresa-uf"
                  className="campo rounded-sistema"
                  style={{ height: 38 }}
                  aria-invalid={erros.uf ? true : undefined}
                  value={rascunho.uf}
                  onChange={(evento) => alterar('uf', evento.target.value)}
                >
                  <option value="">—</option>
                  {UFS.map((uf) => (
                    <option key={uf} value={uf}>
                      {uf}
                    </option>
                  ))}
                </select>
                {erros.uf && <MensagemDeErro>{erros.uf}</MensagemDeErro>}
              </div>
            </div>
          </div>
        </Secao>

      </form>
    </LayoutApp>
  )
}

/* --------------------------------------------------------------- peças */

function Secao({
  numero,
  titulo,
  subtitulo,
  children,
}: {
  numero: string
  titulo: string
  subtitulo: string
  children: ReactNode
}) {
  return (
    <section className="rounded-sistema border border-borda bg-superficie p-5 shadow-[var(--shadow-cartao)]">
      <div className="mb-5 flex items-start gap-3">
        <span className="tabular mt-[2px] flex h-6 w-7 shrink-0 items-center justify-center rounded-[6px] bg-marca-suave text-[11px] font-bold text-marca-forte">
          {numero}
        </span>
        <div className="min-w-0">
          <h2 className="font-titulo text-base font-semibold text-tinta">{titulo}</h2>
          <p className="mt-[2px] text-[12.5px] text-texto-suave">{subtitulo}</p>
        </div>
      </div>

      {children}
    </section>
  )
}

/**
 * A logo, com miniatura, envio e remoção.
 *
 * O `input[type=file]` fica escondido e é acionado pelo botão: o controle
 * nativo não tem como ser estilizado com os tokens, e um botão de verdade
 * anuncia melhor o que vai acontecer. O input continua no DOM com rótulo, para
 * a navegação por teclado e o leitor de tela chegarem nele.
 */
function BlocoDaLogo({
  empresa,
  ocupado,
  erro,
  seletor,
  aoEscolher,
  aoRemover,
}: {
  empresa: Empresa
  ocupado: boolean
  erro?: string
  seletor: RefObject<HTMLInputElement | null>
  aoEscolher: (arquivo: File | undefined) => void
  aoRemover: () => void
}) {
  return (
    <div>
      <span className="mb-2 block text-[13px] font-semibold text-texto">Logo da empresa</span>

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex h-[76px] w-[76px] shrink-0 items-center justify-center overflow-hidden rounded-sistema border border-borda bg-superficie-suave">
          {empresa.logo ? (
            <img
              src={urlDaLogo(empresa.logo)}
              alt={`Logo de ${empresa.nome_fantasia || empresa.nome}`}
              className="h-full w-full object-contain"
            />
          ) : (
            <Icone nome="image" tamanho={26} className="text-inativo" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap gap-2">
            <input
              ref={seletor}
              id="empresa-logo"
              type="file"
              accept={TIPOS_DE_LOGO.join(',')}
              className="sr-only"
              onChange={(evento) => aoEscolher(evento.target.files?.[0])}
            />
            <label
              htmlFor="empresa-logo"
              className="flex h-[34px] cursor-pointer items-center gap-2 rounded-sistema border border-borda bg-superficie px-3.5 text-[13px] font-semibold text-marca hover:border-marca-borda hover:bg-marca-fundo"
            >
              <Icone nome="image" tamanho={16} />
              {empresa.logo ? 'Trocar logo' : 'Enviar logo'}
            </label>

            {empresa.logo && (
              <button
                type="button"
                onClick={aoRemover}
                disabled={ocupado}
                className="flex h-[34px] items-center gap-2 rounded-sistema border border-borda bg-superficie px-3.5 text-[13px] font-semibold text-texto hover:border-erro hover:text-erro disabled:cursor-not-allowed disabled:text-texto-fraco"
              >
                <Icone nome="close" tamanho={16} />
                Remover
              </button>
            )}
          </div>

          <p className="mt-2 text-[12px] leading-[1.5] text-texto-fraco">
            {ocupado
              ? 'Enviando…'
              : empresa.logo
                ? `${empresa.logo.nome_arquivo} · ${emKB(empresa.logo.bytes)} · sai nos documentos impressos`
                : 'PNG ou JPG, até 2 MB. Sai nos documentos impressos.'}
          </p>
        </div>
      </div>

      {erro && <MensagemDeErro>{erro}</MensagemDeErro>}
    </div>
  )
}

/** Mesmo selo dos formulários de cliente e fornecedor. */
function IndicadorBusca() {
  return (
    <span role="status" className="flex items-center gap-1.5 text-[12px] font-medium text-marca">
      <Icone nome="sync" tamanho={13} className="animate-spin" />
      Buscando…
    </span>
  )
}
